/**
 * Shared meter/offer calculation utilities
 *
 * Used by both EA Report Preview and Offer Comparison to ensure
 * Total KWH (consumption) and Contract Cost ($) are calculated consistently.
 *
 * Consumption formula: dailyConsumption × periodDays × (contractType === TARIFF ? 1 : dlf × mlf)
 * i.e. per-meter loss factor for non-TARIFF (C&I) sites.
 *
 * - Input: meter → calculates consumption (period)
 */

import { calculateDailyEnergy } from '~/models/common';
import { ContractType } from '~/models/meter';
import { ReportProjectMeter } from './reportTypes';

export interface ConsumptionOptions {
  /** When true, use per-meter dlf×mlf (and contractType) for consumption. Preferred. */
  usePerMeterLossFactor?: boolean;
  /** Weighted average DLF - used only when usePerMeterLossFactor is false (legacy) */
  lwaDLF?: number;
  /** Weighted average MLF - used only when usePerMeterLossFactor is false (legacy) */
  lwaMLF?: number;
  /** If true, do not apply loss factor (TARIFF sites). Used when usePerMeterLossFactor is false. */
  isTariff?: boolean;
}

/**
 * Calculate meter consumption (Total KWH) for a given period length.
 * Formula: dailyConsumption × numOfDays × (contractType === TARIFF ? 1 : dlf × mlf)
 *
 * When usePerMeterLossFactor is true (default for new code): uses meter's own dlf, mlf and contractType.
 * Otherwise uses lwaDLF×lwaMLF and isTariff from options (legacy).
 */
export function calculateMeterConsumption(
  meter: ReportProjectMeter,
  numOfDays: number,
  options?: ConsumptionOptions
): number {
  const originalTotalEnergy = meter.annualData?.totalEnergy ?? 0;
  const dailyTotalEnergy = calculateDailyEnergy(originalTotalEnergy);
  let consumption = dailyTotalEnergy * (numOfDays || 0);

  if (options?.usePerMeterLossFactor) {
    const isTariff = meter.contract?.contractType === ContractType.TARIFF;
    if (!isTariff) {
      const dlf = meter.dlf ?? meter.tlf ?? 1;
      const mlf = meter.mlf ?? meter.tlf ?? 1;
      consumption *= dlf * mlf;
    }
  } else if (options?.lwaDLF != null && options?.lwaMLF != null && !options?.isTariff) {
    consumption *= options.lwaDLF * options.lwaMLF;
  }

  // Round to fixed precision so EA Report and Offer Comparison show identical Total KWH (avoid float drift)
  return Math.round(consumption * 1e4) / 1e4;
}

/** Same formula as calculateMeterConsumption but returns peak/offPeak/shoulder/total for EA Report Bill display */
export function calculateMeterConsumptionByComponent(
  meter: ReportProjectMeter,
  numOfDays: number,
  options?: ConsumptionOptions
): { peak: number; offPeak: number; shoulder: number; total: number } {
  const dailyPeak = calculateDailyEnergy(meter.annualData?.peakEnergy ?? 0);
  const dailyOffPeak = calculateDailyEnergy(meter.annualData?.offPeakEnergy ?? 0);
  const dailyShoulder = calculateDailyEnergy(meter.annualData?.shoulderEnergy ?? 0);
  let mult = 1;
  if (options?.usePerMeterLossFactor) {
    const isTariff = meter.contract?.contractType === ContractType.TARIFF;
    if (!isTariff) {
      const dlf = meter.dlf ?? meter.tlf ?? 1;
      const mlf = meter.mlf ?? meter.tlf ?? 1;
      mult = dlf * mlf;
    }
  } else if (options?.lwaDLF != null && options?.lwaMLF != null && !options?.isTariff) {
    mult = options.lwaDLF * options.lwaMLF;
  }
  const peak = Math.round(dailyPeak * (numOfDays || 0) * mult * 1e4) / 1e4;
  const offPeak = Math.round(dailyOffPeak * (numOfDays || 0) * mult * 1e4) / 1e4;
  const shoulder = Math.round(dailyShoulder * (numOfDays || 0) * mult * 1e4) / 1e4;
  const total = calculateMeterConsumption(meter, numOfDays, options);
  return { peak, offPeak, shoulder, total };
}
